/**
 * Single-render bootstrap entry (design doc §5.2).
 *
 * Replaces main.tsx + PrototypeFidelityApp.tsx with a plain TypeScript entry.
 *
 * Mode A (SSR direct): Topic/Chapter routes are served by renderAppDocument,
 *   which outputs the full page shell + pre-filled view DOM inside [data-app-root].
 *   bootstrapApp detects child nodes -> seeds topicStore from #page-model JSON
 *   -> binds interactions.
 *
 * Mode B (SPA fallback): Other routes (Browse/Community/Pricing/etc.) are served
 *   by serveSpaShell (returns index.html with empty #root). bootstrapApp detects
 *   empty root -> injects prototypeMarkup (all 28 markup templates) -> binds.
 */

import "../styles/tokens.css";
import "../styles/prototype-full.css";
import { installDynamicImportRecovery } from './dynamicImportRecovery';
import { prototypeMarkup } from "./prototype/prototypeMarkup";
import { mountPrototypeController } from "./prototype/PrototypeController";
import { readPageModel, isSsrDirectMode } from "./prototype/pageModelReader";
import { seedFromPageModel } from "./prototype/topicStore";
import { isAppBootstrapped, markAppBootstrapped } from "./prototype/appBootstrapGuard";
import { mountSsrMediaSurfaces } from './prototype/businessOverlayLifecycle';
import { applyAndroidViewportInsetPolicy } from './prototype/androidViewportInsets';

installDynamicImportRecovery(window, fetch);

async function bootstrapApp(): Promise<void> {
	// Some Android browsers expose their own collapsing toolbar through the
	// safe-area environment variables. Mark the platform before any app chrome
	// is measured so CSS can reserve only the actual page viewport.
	applyAndroidViewportInsetPolicy(document);
	// Idempotency guard: Vite dynamic chunks import the entry bundle at its bare
	// URL, so any URL mismatch on the HTML <script> tag (e.g. a ?v= query)
	// instantiates this whole module graph twice and re-runs bootstrapApp(). The
	// first instance owns the DOM — re-injecting the shell would duplicate every
	// view/mount and re-binding the controller would double every listener.
	if (isAppBootstrapped(document.body)) return;
	if (isSsrDirectMode()) {
		// Mode A: SSR direct (Topic / Chapter)
		// Server has output the full page shell + pre-filled view DOM.
		// Seed the topicStore from #page-model JSON so showTopicDetail
		// can paint before its matching navigation projection is read.
		const pageModel = readPageModel();
		if (pageModel) {
			if (pageModel.pageType === "topic") {
				seedFromPageModel(pageModel.topic, pageModel.chapters);
			} else if (pageModel.pageType === "chapter") {
				seedFromPageModel(pageModel.topic, pageModel.directory);
			}
		}
		// SSR keeps heavy media markup out of the response. Mount it before the
		// controller wires player and video interactions.
		mountSsrMediaSurfaces(document.body);
		// Bind interactions to the existing DOM.
		mountPrototypeController(document.body);
	} else {
		// Mode B: SPA fallback (Browse / Community / Pricing / Profile / Settings / etc.)
		// serveSpaShell returned index.html with an empty #root.
		// Inject the full prototypeMarkup (shell + all views + player + modals).
		const template = document.createElement("template");
		template.innerHTML = prototypeMarkup;
		const mountedNodes = [...template.content.childNodes];
		for (const node of mountedNodes) document.body.appendChild(node);
		// mountPrototypeController on document.body binds all events.
		mountPrototypeController(document.body);
	}
	// Stamp the root synchronously so any re-instantiated entry module no-ops.
	markAppBootstrapped(document.body);
	window.dispatchEvent(new CustomEvent('deepmox:app-ready'));
}

// Auto-bootstrap on module load (replaces React's createRoot().render())
void bootstrapApp();
